import React, { createContext, useContext, useEffect, useState } from "react";
import { addDays, subDays } from "date-fns";
import { EventTypes } from "utilFunctions/Constants/Constants";
import { ViewMode } from "Pages/MyScheduleBeta/Components/FiltersSchedule/DayWeekTab";
import { DAY_WEEK_TYPE } from "Pages/MyScheduleBeta/utils/constants";
import { toISTStart, toISTEnd } from "Pages/MyScheduleBeta/utils/dateHelpers";
import { useLocation, useNavigate } from "react-router-dom";

// ─── Session persistence helpers ─────────────────────────────────────────────

const SESSION_KEY = "scheduleBetaState";

interface PersistedState {
  selectedDate?: string;
  selectedEventType?: string;
  isNotmarkedVisible?: boolean;
  filterState?: Record<string, any>;
  eventType?: ViewMode;
  activeTab?: string;
  searchedFaculty?: string | null;
}

const saveToSession = (partial: PersistedState) => {
  try {
    const existing = getFromSession();
    sessionStorage.setItem(
      SESSION_KEY,
      JSON.stringify({ ...existing, ...partial })
    );
  } catch (_) {
    // sessionStorage unavailable — silently ignore
  }
};

const getFromSession = (): PersistedState => {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    return raw ? (JSON.parse(raw) as PersistedState) : {};
  } catch (_) {
    return {};
  }
};

// ─── Types ────────────────────────────────────────────────────────────────────

export interface DateRange {
  startDate: Date;
  endDate: Date;
  key: string;
}

export interface ScheduleBetaContextValue {
  /** Currently selected single date (YYYY-MM-DD) */
  selectedDate: string;
  setSelectedDate: React.Dispatch<React.SetStateAction<string>>;

  /** Today's date as YYYY-MM-DD in IST */
  todayDate: string;

  /** Active event-type filter (e.g. "ALL", "LECTURE") */
  selectedEventType: string;
  setSelectedEventType: React.Dispatch<React.SetStateAction<string>>;

  /** Whether "Not Marked" toggle is active */
  isNotmarkedVisible: boolean;
  setIsNotmarkedVisible: React.Dispatch<React.SetStateAction<boolean>>;

  /** Faculty being searched (employeeId) – null means own schedule */
  searchedFaculty: string | null;
  setSearchedFaculty: React.Dispatch<React.SetStateAction<string | null>>;

  /** Currently active tab key */
  activeTab: string;
  setActiveTab: React.Dispatch<React.SetStateAction<string>>;

  /** Schedules for the selected date */
  todaySchedules: any[];
  setTodaySchedules: React.Dispatch<React.SetStateAction<any[]>>;

  /** Full schedules map from API response (date -> event list) */
  fullSchedules: Record<string, any[]>;
  setFullSchedules: React.Dispatch<React.SetStateAction<Record<string, any[]>>>;

  /**
   * Shared filter state consumed by useMyScheduleBeta to build API URLs.
   * startDate / endDate are IST-boundary strings: "YYYY-MM-DD HH:mm:ss"
   */
  filterState: Record<string, any>;
  setFilterState: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  eventType: any;
  setEventType: any;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Returns today's date as "YYYY-MM-DD" in Asia/Kolkata timezone */
const getTodayDate = (): string => {
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone: "Asia/Kolkata",
  };
  const formatted = new Intl.DateTimeFormat("en-IN", options).format(
    new Date()
  );
  const [day, month, year] = formatted.split("/");
  return `${year}-${month}-${day}`;
};

// ─── Context ──────────────────────────────────────────────────────────────────

const ScheduleBetaContext = createContext<ScheduleBetaContextValue | undefined>(
  undefined
);

// ─── Provider ─────────────────────────────────────────────────────────────────

interface ScheduleBetaProviderProps {
  children: React.ReactNode;
}
export const INITIAL_WEEK_START_DATE = toISTStart(subDays(new Date(), 3));
export const INITIAL_WEEK_END_DATE = toISTEnd(addDays(new Date(), 3));
export const ScheduleBetaProvider = ({
  children,
}: ScheduleBetaProviderProps) => {
  const todayDate = getTodayDate();
  const location = useLocation();
  const navigate = useNavigate();

  const returningFromDetails =
    (location.state as any)?.fromLectureDetails === true;
  // Read persisted state once at mount time
  const persisted = returningFromDetails ? getFromSession() : {};

  /** The single date whose schedule cards are shown */
  const [selectedDate, setSelectedDate] = useState<string>(
    persisted.selectedDate ?? todayDate
  );

  /** Event type filter — persists across tab switches */
  const [selectedEventType, setSelectedEventType] = useState<string>(
    persisted.selectedEventType ?? EventTypes[0]?.value ?? "ALL"
  );

  /** Unmarked lectures toggle */
  const [isNotmarkedVisible, setIsNotmarkedVisible] = useState<boolean>(false);

  /** Search-by-faculty state — null = own schedule */
  const [searchedFaculty, setSearchedFaculty] = useState<string | null>(
    persisted.searchedFaculty ?? null
  );

  /** Active tab (event-type tab bar) */
  const [activeTab, setActiveTab] = useState<string>(
    persisted.activeTab ?? "1"
  );

  /** Schedules for the selected date */
  const [todaySchedules, setTodaySchedules] = useState<any[]>([]);

  const [fullSchedules, setFullSchedules] = useState<Record<string, any[]>>({});

  /** Shared filter state — startDate/endDate written by the header date picker */
  const [filterState, setFilterState] = useState<Record<string, any>>(
    persisted.filterState ?? {
      eventType: EventTypes[0]?.value ?? "ALL",
      startDate: toISTStart(new Date()),
      endDate: toISTEnd(new Date()),
      subject: null,
      facultySearch: "",
    }
  );
  const [eventType, setEventType] = useState<ViewMode>(
    persisted.eventType ?? DAY_WEEK_TYPE.DAY
  );

  useEffect(() => {
    if (returningFromDetails) {
      navigate(location.pathname, {
        replace: true,
        state: null,
      });
    }
  }, [returningFromDetails]);

  // ── Persist to sessionStorage whenever state changes ─────────────────────

  useEffect(() => {
    saveToSession({ selectedDate });
  }, [selectedDate]);

  useEffect(() => {
    saveToSession({ selectedEventType });
  }, [selectedEventType]);

  useEffect(() => {
    saveToSession({ filterState });
  }, [filterState]);

  useEffect(() => {
    saveToSession({ eventType });
  }, [eventType]);

  useEffect(() => {
    saveToSession({ activeTab });
  }, [activeTab]);

  useEffect(() => {
    saveToSession({ searchedFaculty });
  }, [searchedFaculty]);

  const value: ScheduleBetaContextValue = {
    selectedDate,
    setSelectedDate,
    todayDate,
    selectedEventType,
    setSelectedEventType,
    isNotmarkedVisible,
    setIsNotmarkedVisible,
    searchedFaculty,
    setSearchedFaculty,
    activeTab,
    setActiveTab,
    todaySchedules,
    setTodaySchedules,
    fullSchedules,
    setFullSchedules,
    filterState,
    setFilterState,
    eventType,
    setEventType,
  };

  return (
    <ScheduleBetaContext.Provider value={value}>
      {children}
    </ScheduleBetaContext.Provider>
  );
};

// ─── Consumer hook ────────────────────────────────────────────────────────────

/**
 * Access the shared /admin/schedule-beta context.
 * Must be used inside <ScheduleBetaProvider>.
 */
export const useScheduleBetaContext = (): ScheduleBetaContextValue => {
  const context = useContext(ScheduleBetaContext);
  if (context === undefined) {
    throw new Error(
      "useScheduleBetaContext must be used within a <ScheduleBetaProvider>"
    );
  }
  return context;
};

export default ScheduleBetaContext;
